import { useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { updateWithVersion, isStaleWriteError } from '@/lib/optimistic'
import { ValidationError } from '@/lib/errorReporter'
import { useNavigate } from 'react-router-dom'
import { confirmDialog, choiceDialog, promptDialog, alertDialog } from '@/components/Dialogs'
import { fetchRunPreflight, preflightConfirmMessage, NO_REJECTS_ISSUE } from '@/lib/runPreflight'
import type { RunActionKind } from '@/lib/runStatus'
import type { Run } from '@/types/database'

// The run lifecycle writes (stop tipping, reopen, complete with preflight),
// taken out of RunsPage so every surface that offers RunActionButtons (register
// cards, Table rows, the run page, a manager's station header) runs the SAME
// writes: updateWithVersion + update_reason, the same cache invalidation, the
// same preflight. The who-may-do-what guard stays in runActions
// (src/lib/runStatus.ts) and in the server; this hook only performs the writes.
export type LifecycleRun = Run

export function useRunLifecycle(opts: {
  /** Fires after Complete run lands (the register shows its "done" card). */
  onCompleted?: (r: LifecycleRun) => void
  /** Fires when a reopen is refused (the register also shows it inline). */
  onReopenError?: (e: Error) => void
  /** Viewer may open Rejects (U05 UF-03): the no-rejects dialog then offers "Log rejects for this run". Left out where Rejects is not offered (Tipping), so no module-access read runs there. */
  canOpenRejects?: boolean | null
} = {}) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const canOpenRejects = opts.canOpenRejects

  const invalidate = () => {
    // The run-detail surfaces (pallets, tipping, rejects, labour shift
    // summaries) all key off the run's status; a stale child cache shows
    // yesterday's button beside today's banner.
    qc.invalidateQueries({ queryKey: ['runs'] })
    qc.invalidateQueries({ queryKey: ['run-pallets'] })
    qc.invalidateQueries({ queryKey: ['run-tipping'] })
    qc.invalidateQueries({ queryKey: ['run-rejects'] })
    qc.invalidateQueries({ queryKey: ['run-labour-shifts'] })
  }

  async function write(r: LifecycleRun, patch: (userId: string) => Record<string, unknown>) {
    const { data: { session } } = await supabase.auth.getSession()
    const user = session?.user
    if (!user) throw new ValidationError('Not authenticated')
    try {
      await updateWithVersion('runs', { col: 'run_id', value: r.run_id }, r.version, patch(user.id))
    } catch (e) {
      if (isStaleWriteError(e)) {
        throw new ValidationError('Run was just updated by someone else — refresh and try again.')
      }
      throw e
    }
  }

  // Stop tipping: ACTIVE -> TIPPING_COMPLETE. Packing + rejects continue.
  const stopTipping = useMutation({
    mutationFn: (r: LifecycleRun) => write(r, uid => ({
      status:             'TIPPING_COMPLETE',
      tipping_stopped_at: new Date().toISOString(),
      tipping_stopped_by: uid,
      update_reason:      'stop tipping (manual)',
    })),
    onSuccess: invalidate,
  })

  // Reopen for tipping: TIPPING_COMPLETE (manager) / COMPLETE (admin) -> ACTIVE.
  // Clears the stamps; the mig 380 audit table records the reopen.
  const reopenForTipping = useMutation({
    mutationFn: (r: LifecycleRun) => write(r, () => ({
      status:             'ACTIVE',
      tipping_stopped_at: null,
      tipping_stopped_by: null,
      completed_at:       null,
      completed_by:       null,
      update_reason:      'reopen for tipping',
    })),
    onSuccess: invalidate,
    onError: (e: Error) => opts.onReopenError?.(e),
  })

  // Complete run: TIPPING_COMPLETE (or ACTIVE) -> COMPLETE. Ready for Verify.
  const completeRun = useMutation({
    mutationFn: (r: LifecycleRun) => write(r, uid => ({
      status:        'COMPLETE',
      completed_at:  new Date().toISOString(),
      completed_by:  uid,
      update_reason: 'complete run',
    })),
    onSuccess: (_d, r) => { opts.onCompleted?.(r); invalidate() },
  })

  // R1b: Archive a run made in error. ACTIVE / TIPPING_COMPLETE -> ARCHIVED,
  // with a reason kept in runs.update_reason. Only a run with nothing tipped and
  // no pallets: the counts are re-read here (the register row can be stale), and
  // a read that fails refuses, never archives. Uses the existing status; the
  // runs_sensitive_col_gate trigger already limits status writes to manager+.
  const archiveRun = useMutation({
    mutationFn: ({ r, reason }: { r: LifecycleRun; reason: string }) => write(r, () => ({
      status:        'ARCHIVED',
      update_reason: `archive run: ${reason}`,
    })),
    onSuccess: invalidate,
  })

  const archiveWithReason = async (r: LifecycleRun) => {
    const [tips, pallets] = await Promise.all([
      supabase.from('tipping_log').select('tipping_id', { count: 'exact', head: true }).eq('run_id', r.run_id),
      supabase.from('pallets').select('pallet_id', { count: 'exact', head: true }).eq('run_id', r.run_id).is('deleted_at', null),
    ])
    if (tips.error || pallets.error || tips.count == null || pallets.count == null) {
      await alertDialog({ title: `Could not check ${r.run_number}`, message: 'The app could not read what this run holds, so it did not archive it. Try again.' })
      return
    }
    if (tips.count > 0 || pallets.count > 0) {
      await alertDialog({
        title: `${r.run_number} cannot be archived`,
        message: 'This run has fruit tipped or pallets on it. Archive is only for a run made in error. Use Complete Run to close it.',
      })
      return
    }
    const reason = await promptDialog({
      title: `Archive ${r.run_number}?`,
      message: 'Nothing was tipped and no pallets were built. The run leaves the active list. You cannot undo this in the app.',
      placeholder: 'Reason, for example: made on the wrong fruit',
      confirm: 'Archive run',
      tone: 'danger',
    })
    const text = reason?.trim()
    if (!text) return
    archiveRun.mutate({ r, reason: text })
  }

  // Complete-run pre-flight (plan §2D 6): say what is still open, never block.
  // S1: the reads below run before any dialog and `busy` only covers a pending
  // write, so a second tap on slow wifi would start a second pre-flight and a
  // second dialog. A ref (not state) closes the gap on the same tick.
  const completing = useRef(false)
  const completeWithPreflight = async (r: LifecycleRun) => {
    if (completing.current) return
    completing.current = true
    try { await runCompleteWithPreflight(r) } finally { completing.current = false }
  }
  const runCompleteWithPreflight = async (r: LifecycleRun) => {
    const issues = await fetchRunPreflight(supabase, r)
    if (issues.length > 0) {
      const title   = `Complete ${r.run_number} with open items?`
      const message = preflightConfirmMessage(issues)
      if (issues.includes(NO_REJECTS_ISSUE) && canOpenRejects === true) {
        // U05 UF-03: the dialog names the gap, so it also offers the way to
        // close it. Cancel / Escape do nothing; rejects open on THIS run.
        const pick = await choiceDialog({
          title, message, tone: 'warning',
          options: [
            { value: 'log',      label: 'Log rejects for this run' },
            { value: 'complete', label: 'Complete anyway' },
          ],
        })
        if (pick === 'log') { navigate(`/rejects?run=${encodeURIComponent(r.run_id)}`); return }
        if (pick !== 'complete') return
      } else {
        const ok = await confirmDialog({ title, message, confirm: 'Complete anyway', tone: 'warning' })
        if (!ok) return
      }
    }
    completeRun.mutate(r)
  }

  /** Route a RunActionButtons choice to its write. */
  const act = (kind: RunActionKind, r: LifecycleRun) => {
    if (kind === 'stopTipping') stopTipping.mutate(r)
    else if (kind === 'completeRun') void completeWithPreflight(r)
    else if (kind === 'archive') void archiveWithReason(r)
    else reopenForTipping.mutate(r)
  }

  const busy = stopTipping.isPending || completeRun.isPending || reopenForTipping.isPending || archiveRun.isPending
  return { archiveRun, archiveWithReason, stopTipping, reopenForTipping, completeRun, completeWithPreflight, act, busy }
}

export type RunLifecycle = ReturnType<typeof useRunLifecycle>
